import { useState, useRef, useCallback } from 'react';
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import {
  PROFILE_IMAGE_MIME_TYPES,
  FILE_SIZE_LIMITS,
} from '@/constants/file-upload';

interface ImageUploadProps {
  onImageSelect: (file: File) => void;
  currentImageUrl?: string;
  maxSizeMB?: number;
  acceptedFormats?: string[];
  className?: string;
  error?: string;
}

export default function ImageUpload({
  onImageSelect,
  currentImageUrl,
  maxSizeMB = FILE_SIZE_LIMITS.PROFILE_IMAGE_MAX_SIZE_MB,
  acceptedFormats = [...PROFILE_IMAGE_MIME_TYPES],
  className,
  error,
}: ImageUploadProps) {
  const [preview, setPreview] = useState<string | null>(currentImageUrl ?? null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback((file: File): string | null => {
    // Check file type
    if (!acceptedFormats.includes(file.type)) {
      return `Please upload an image in one of these formats: ${acceptedFormats.map(f => f.split('/')[1]).join(', ')}`;
    }

    // Check file size
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > maxSizeMB) {
      return `File size must be less than ${maxSizeMB}MB. Current size: ${sizeMB.toFixed(2)}MB`;
    }

    return null;
  }, [acceptedFormats, maxSizeMB]);

  const handleFileChange = useCallback((file: File) => {
    setUploadError(null);

    const error = validateFile(file);
    if (error) {
      setUploadError(error);
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Pass file to parent
    onImageSelect(file);
  }, [onImageSelect, validateFile]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileChange(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileChange(file);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const displayError = error ?? uploadError;

  return (
    <div className={cn('w-full', className)}>
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedFormats.join(',')}
        onChange={handleInputChange}
        className="hidden"
      />

      {preview ? (
        <div className="relative">
          <div className="relative w-32 h-32 mx-auto">
            <img
              src={preview}
              alt="Profile preview"
              className="w-full h-full object-contain rounded-lg border-2 border-secondary-200 dark:border-secondary-700"
            />
            <button
              type="button"
              onClick={handleRemove}
              className="absolute -top-2 -right-2 p-1.5 bg-danger-500 hover:bg-danger-600 text-white rounded-full shadow-lg transition-colors"
              title="Remove image"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
          <p className="text-center mt-3 text-sm text-secondary-600 dark:text-secondary-400">
            Click the X to remove or{' '}
            <button
              type="button"
              onClick={handleClick}
              className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
            >
              upload a different image
            </button>
          </p>
        </div>
      ) : (
        <div
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            'relative cursor-pointer rounded-lg border-2 border-dashed p-8 transition-colors',
            isDragging
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
              : displayError
              ? 'border-danger-300 bg-danger-50 dark:border-danger-600 dark:bg-danger-900/20'
              : 'border-secondary-300 hover:border-primary-400 dark:border-secondary-600 dark:hover:border-primary-500'
          )}
        >
          <div className="flex flex-col items-center justify-center space-y-3">
            <div className={cn(
              'w-16 h-16 rounded-full flex items-center justify-center',
              isDragging
                ? 'bg-primary-100 dark:bg-primary-900/40'
                : displayError
                ? 'bg-danger-100 dark:bg-danger-900/40'
                : 'bg-secondary-100 dark:bg-secondary-800'
            )}>
              <PhotoIcon className={cn(
                'w-8 h-8',
                isDragging
                  ? 'text-primary-600 dark:text-primary-400'
                  : displayError
                  ? 'text-danger-600 dark:text-danger-400'
                  : 'text-secondary-400 dark:text-secondary-500'
              )} />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-secondary-900 dark:text-white">
                {isDragging ? 'Drop image here' : 'Click to upload or drag and drop'}
              </p>
              <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                {acceptedFormats.map(f => f.split('/')[1].toUpperCase()).join(', ')} up to {maxSizeMB}MB
              </p>
            </div>
          </div>
        </div>
      )}

      {displayError && (
        <p className="mt-2 text-sm text-danger-600 dark:text-danger-400">{displayError}</p>
      )}
    </div>
  );
}
